import React, { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirestore, collection, getDocs, query, orderBy } from "firebase/firestore";
import { useUserRole } from "../hooks/useUserRole";
import "../styles/AdminDashboard.css";

interface UserData {
  userId: string;
  email: string;
  displayName: string;
  role: string;
  createdAt: Date;
}

export function AdminDashboard() {
  const { role } = useUserRole();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [promoteLoading, setPromoteLoading] = useState<string | null>(null);

  const functions = getFunctions();
  const db = getFirestore();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all users from Firestore
      const usersCollection = collection(db, "users");
      const q = query(usersCollection, orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);

      const userList: UserData[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        userList.push({
          userId: data.userId,
          email: data.email || "",
          displayName: data.displayName || "",
          role: data.role || "END_USER",
          createdAt: data.createdAt?.toDate?.() || new Date(),
        });
      });

      setUsers(userList);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
      console.error("Error loading users:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteToAdmin = async (userId: string, email: string) => {
    try {
      setPromoteLoading(userId);
      const promoteToAdminFn = httpsCallable(functions, "promote_to_admin");
      const result = await promoteToAdminFn({ userId });

      if ((result.data as any).success) {
        // Reload users
        await loadUsers();
        alert(`✅ ${email} promoted to ADMIN`);
      }
    } catch (err) {
      alert(`❌ Error: ${err instanceof Error ? err.message : "Failed to promote user"}`);
    } finally {
      setPromoteLoading(null);
    }
  };

  if (role !== "ADMIN" && role !== "CLUB") {
    return (
      <div className="admin-page">
        <h1>Access Denied</h1>
        <p>You need admin privileges to access this page.</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <h1>👥 Admin Dashboard</h1>

      <div className="admin-section">
        <h2>User Management</h2>

        {error && <div className="error-message">{error}</div>}

        <div className="users-table-container">
          {loading ? (
            <div className="spinner"></div>
          ) : users.length === 0 ? (
            <p>No users found</p>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Display Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.userId} className={`user-row role-${u.role.toLowerCase()}`}>
                    <td className="name">{u.displayName || "—"}</td>
                    <td className="email">{u.email}</td>
                    <td className="role">
                      <span className={`role-badge ${u.role.toLowerCase()}`}>{u.role}</span>
                    </td>
                    <td className="created">{u.createdAt.toLocaleDateString("no-NO")}</td>
                    <td className="actions">
                      {u.role !== "ADMIN" && (
                        <button
                          className="btn-promote"
                          onClick={() => handlePromoteToAdmin(u.userId, u.email)}
                          disabled={promoteLoading === u.userId}
                        >
                          {promoteLoading === u.userId ? "⏳" : "👑 Promote"}
                        </button>
                      )}
                      {u.role === "ADMIN" && <span className="role-badge admin">Admin</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <button className="btn-refresh" onClick={loadUsers} disabled={loading}>
          🔄 Refresh
        </button>
      </div>
    </div>
  );
}
